// Textile Modern: a compact retail label for clothing, drawn after the approved
// reference. Colour name over a thin rule with the product code beneath, a
// rounded size badge, a small "ÇMIMI" caption over the price, thin vertical
// dividers between the three, and the CODE128 barcode along the bottom.
// No images and no materials text.
//
// Printing: every colour is optional decoration over black text. Badge tints
// are light or dark enough that the dark/white text keeps its contrast when the
// printer converts to grayscale, and unknown colours fall back to a black badge.
//
// It draws only inside the card. Paper, grid, margins, gaps and pagination come
// from the sheet; the barcode SVG is the shared renderer's output, untouched.

import { BARCODE_LABEL_TEMPLATE, normalizeDimensionKey } from "../barcodeLabelTemplate";
import { escapeHTML } from "../html";
import { getQrModuleMM, QR_MIN_MODULE_MM, renderQrSvg } from "../qrCode";

// One source for the numbers the CSS draws with and the fit check measures.
export const TEXTILE_LAYOUT = Object.freeze({
  mainRowMM: 18,
  rowGapMM: 2,
  badgeSizeMM: 10.5,
  badgeMaxWidthMM: 20,
  columnGapMM: 2.2,
  dividerHeightMM: 11,
  ruleMM: 0.24,
  barcodeHeightMM: 10.5,
  barcodeSidePaddingMM: 1.6,
  barcodeSpacingMM: 0.55,
  barcodeTextPt: 5,
  // Optional product QR, at the right end of the upper row. It is omitted when
  // the id column (colour + code) would be left narrower than minIdColumnMM.
  qrBoxMM: 12,
  qrColumnGapMM: 1.6,
  priceColumnMM: 20,
  minIdColumnMM: 13,
  // Slack so rounding in the browser never pushes the barcode past the card.
  safetyMM: 0.8,
});

// The same design for short labels (for example 63.5 × 25.4 mm, 3 × 11 per
// sheet): the same order and look, tighter rows, smaller type. It is chosen
// automatically when the regular layout does not fit the label's height.
export const TEXTILE_COMPACT_LAYOUT = Object.freeze({
  ...TEXTILE_LAYOUT,
  topInsetMM: 0.5,
  mainRowMM: 12.5,
  rowGapMM: 0.8,
  badgeSizeMM: 9.5,
  dividerHeightMM: 9,
  columnGapMM: 2,
  barcodeHeightMM: 7,
  barcodeSpacingMM: 0.45,
  barcodeTextPt: 5,
  qrBoxMM: 10,
  priceColumnMM: 16,
  safetyMM: 0.6,
});

const PT_TO_MM = 25.4 / 72;
// The card draws a border inside its box, on top of the sheet's padding.
const CARD_BORDER_MM = 0.32;

/** Height the stacked rows need inside the card's padding. */
export const getTextileRequiredHeightMM = (layout = TEXTILE_LAYOUT) => {
  const barcodeText = layout.barcodeTextPt * PT_TO_MM * 1.15;
  return (
    (layout.topInsetMM || 0) +
    layout.mainRowMM +
    layout.rowGapMM +
    layout.barcodeHeightMM +
    layout.barcodeSpacingMM +
    barcodeText +
    layout.safetyMM
  );
};

/** Width the bars are stretched across; the density check measures against it. */
export const getTextileBarcodeDrawableWidthMM = (labelWidthMM, geometry) => {
  const left = Number(geometry?.paddingLeftMM ?? BARCODE_LABEL_TEMPLATE.cardPadMM);
  const right = Number(geometry?.paddingRightMM ?? BARCODE_LABEL_TEMPLATE.cardPadMM);
  return labelWidthMM - left - right - TEXTILE_LAYOUT.barcodeSidePaddingMM * 2;
};

const getUsableHeightMM = (geometry) =>
  Number(geometry?.labelHeightMM || 0) -
  Number(geometry?.paddingTopMM || 0) -
  Number(geometry?.paddingBottomMM || 0) -
  CARD_BORDER_MM * 2;

/** "regular" when its rows fit the label's height, otherwise "compact". */
export const getTextileLayoutName = (geometry) =>
  getTextileRequiredHeightMM(TEXTILE_LAYOUT) <= getUsableHeightMM(geometry) + 0.001 ? "regular" : "compact";

/**
 * Whether the optional QR fits this label, and the layout it would use. It
 * never grows the label or squeezes the text: if the upper row cannot hold it
 * and still leave the colour and code their minimum width, there is no QR.
 */
export const getTextileQrPlan = (geometry) => {
  const name = getTextileLayoutName(geometry);
  const layout = name === "regular" ? TEXTILE_LAYOUT : TEXTILE_COMPACT_LAYOUT;
  const innerWidth =
    Number(geometry?.labelWidthMM || 0) -
    Number(geometry?.paddingLeftMM ?? BARCODE_LABEL_TEMPLATE.cardPadMM) -
    Number(geometry?.paddingRightMM ?? BARCODE_LABEL_TEMPLATE.cardPadMM) -
    CARD_BORDER_MM * 2;
  const idColumn =
    innerWidth -
    layout.badgeSizeMM -
    layout.ruleMM * 2 -
    layout.priceColumnMM -
    layout.qrBoxMM -
    layout.qrColumnGapMM * 5;
  return {
    layoutName: name,
    boxMM: layout.qrBoxMM,
    idColumnMM: idColumn,
    fits: layout.qrBoxMM <= layout.mainRowMM && idColumn >= layout.minIdColumnMM,
  };
};

// Issue codes are the ones the Print Center already translates.
export const validateTextileFit = (geometry) => {
  const issues = [];
  if (getTextileBarcodeDrawableWidthMM(Number(geometry?.labelWidthMM || 0), geometry) <= 0) {
    issues.push("barcode_width_not_positive");
  }
  const layout = getTextileLayoutName(geometry) === "regular" ? TEXTILE_LAYOUT : TEXTILE_COMPACT_LAYOUT;
  if (getTextileRequiredHeightMM(layout) > getUsableHeightMM(geometry) + 0.001) issues.push("content_height_overflow");
  return issues;
};

// Font steps by text length, so a long code or colour shrinks instead of
// being cut at its first characters. Anything past the last step is clipped
// with an ellipsis, never wrapped.
const tier = (text, steps) => steps.find(([max]) => text.length <= max)?.[1] ?? steps[steps.length - 1][1];
const codeTier = (text) => tier(text, [[5, "lg"], [8, "md"], [11, "sm"], [Infinity, "xs"]]);
const colorTier = (text) => tier(text, [[6, "lg"], [9, "md"], [Infinity, "sm"]]);
const sizeTier = (text) => tier(text, [[2, "lg"], [3, "md"], [5, "sm"], [Infinity, "xs"]]);
const priceTier = (text) => tier(text, [[7, "lg"], [Infinity, "sm"]]);

// Badge tint by colour name (Albanian, Turkish and English spellings). First
// match wins; anything else is the black badge.
const BADGE_TONES = [
  ["rose", ["pembe", "roze", "pink"]],
  ["navy", ["teget", "navy", "blumarin"]],
  ["light", ["bardhe", "white", "ecru", "krem", "cream"]],
];
export const getTextileBadgeTone = (colorName) => {
  const key = normalizeDimensionKey(colorName);
  return BADGE_TONES.find(([, names]) => key && names.some((name) => key.includes(name)))?.[0] ?? "ink";
};

// "49.90€" (the shared price format) -> "€ 49.90", the reference's order.
const priceValue = (price) => {
  const amount = String(price || "").replace(/\s*€$/, "").trim();
  return amount ? `€ ${amount}` : "";
};

// With a QR the id column (colour + code) is only what the badge, price and QR
// leave over, so each text takes the largest step whose estimated width fits it.
// Estimates are deliberately generous (glyph width as a fraction of the font
// size, tracking included); the last step wraps to two lines instead of clipping.
const MM_PER_PT = 25.4 / 72;
// Bold digits run about 0.55 em, bold capitals about 0.68 em; tracked colour names more.
const CODE_STEPS = [["lg", 14], ["md", 11], ["sm", 9]];
const COLOR_STEPS = [["lg", 9, 0.92], ["md", 7.5, 0.86]];
const codeEms = (text) => [...text].reduce((sum, char) => sum + (/[0-9]/.test(char) ? 0.58 : 0.7), 0);
const fitStep = (text, steps, availableMM, lastName, ems) =>
  steps.find(([, pt, em]) => (ems ? ems(text) : text.length * em) * pt * MM_PER_PT <= availableMM)?.[0] ?? lastName;
const BADGE_EXTRA_MM = { lg: 0, md: 0, sm: 2.5, xs: 6 };
const PRICE_EM = 0.5;

/** The id column's width once this label's badge and price are known. */
const estimateIdColumnMM = (plan, layout, size, price) => {
  // No price still keeps the "ÇMIMI" caption's width.
  // The price steps down from 13 to 10.5 pt after seven characters (priceTier).
  const priceMM = price ? price.length * (price.length > 7 ? 10.5 : 13) * MM_PER_PT * PRICE_EM : 7;
  return plan.idColumnMM - BADGE_EXTRA_MM[sizeTier(size)] + (layout.priceColumnMM - priceMM);
};

// The QR is drawn only when the label has a link, the upper row has room for it
// and its modules print at a scannable size. Otherwise the label is unchanged.
const resolveQr = (label, geometry, size, price) => {
  if (!label.qrUrl || !geometry) return null;
  const plan = getTextileQrPlan(geometry);
  if (!plan.fits) return null;
  const qr = renderQrSvg(label.qrUrl);
  if (!qr.ok || getQrModuleMM(plan.boxMM, qr.modules) < QR_MIN_MODULE_MM) return null;
  const layout = plan.layoutName === "regular" ? TEXTILE_LAYOUT : TEXTILE_COMPACT_LAYOUT;
  const idColumnMM = estimateIdColumnMM(plan, layout, size, price);
  // A wide badge or price can leave the colour and code too little room: no QR.
  if (idColumnMM < layout.minIdColumnMM) return null;
  return { ...qr, idColumnMM };
};

/** One label card. `label` is getBarcodeLabelData output, `barcodeSvg` the shared barcode render. */
export const renderTextileCard = (label, barcodeSvg, { attributes = "", className = "", geometry } = {}) => {
  const compact = geometry ? getTextileLayoutName(geometry) === "compact" : false;
  const price = priceValue(label.price);
  const qr = resolveQr(label, geometry, label.size || "—", price);
  // The product code leads; without one the product name (real data) stands in.
  const code = label.modelCode || label.productName || "—";
  const size = label.size || "—";
  const color = label.color || "—";

  return `
    <article class="label-card label-card--textile${compact ? " label-card--compact" : ""}${className ? ` ${className}` : ""}" data-template="textile-modern"${attributes}>
      <div class="label-card-inner">
        <section class="tx-main${qr ? " tx-main--qr" : ""}">
          <div class="tx-id">
            <div class="tx-color tx-color--${qr ? fitStep(color, COLOR_STEPS, qr.idColumnMM - 0.5, "sm") : colorTier(color)}">${escapeHTML(color)}</div>
            <div class="tx-code tx-code--${qr ? fitStep(code, CODE_STEPS, qr.idColumnMM - 0.5, "xs", codeEms) : codeTier(code)}">${escapeHTML(code)}</div>
          </div>
          <span class="tx-divider" aria-hidden="true"></span>
          <div class="tx-size tx-size--${sizeTier(size)} tx-size--${getTextileBadgeTone(color)}">${escapeHTML(size)}</div>
          <span class="tx-divider${price ? "" : " tx-divider--hidden"}" aria-hidden="true"></span>
          <div class="tx-price-block${price ? "" : " tx-price-block--empty"}">
            <div class="tx-price-label">ÇMIMI</div>
            <div class="tx-price tx-price--${priceTier(price)}">${escapeHTML(price)}</div>
          </div>
          ${qr ? `<div class="tx-qr" data-qr-url="${escapeHTML(label.qrUrl)}">${qr.svg}</div>` : ""}
        </section>

        <section class="tx-barcode-shell">
          <div class="tx-barcode">${barcodeSvg}</div>
          <div class="tx-barcode-text">${escapeHTML(label.barcode)}</div>
        </section>
      </div>
    </article>
  `;
};

const L = TEXTILE_LAYOUT;
const C = TEXTILE_COMPACT_LAYOUT;

export const TEXTILE_CARD_CSS = `
    .label-card--textile {
      width: var(--label-width);
      height: var(--label-height);
      min-height: 0;
      min-width: 0;
      overflow: hidden;
      border: 0.32mm solid var(--soft-line);
      border-radius: 2.2mm;
      background: #ffffff;
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .label-card--textile .label-card-inner {
      height: 100%;
      padding: var(--padding-top) var(--padding-right) var(--padding-bottom) var(--padding-left);
      display: grid;
      grid-template-rows: ${L.mainRowMM}mm 1fr;
      row-gap: ${L.rowGapMM}mm;
    }

    .tx-main {
      display: grid;
      grid-template-columns: minmax(0, 1fr) ${L.ruleMM}mm auto ${L.ruleMM}mm auto;
      align-items: center;
      column-gap: ${L.columnGapMM}mm;
      height: ${L.mainRowMM}mm;
    }

    .tx-id {
      min-width: 0;
      display: grid;
      gap: 1.1mm;
    }

    .tx-color {
      min-width: 0;
      padding-bottom: 1.2mm;
      border-bottom: ${L.ruleMM}mm solid rgba(17, 17, 17, 0.45);
      font-weight: 500;
      line-height: 1.2;
      letter-spacing: 0.2em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-transform: uppercase;
    }
    .tx-color--lg { font-size: 11pt; }
    .tx-color--md { font-size: 8.5pt; letter-spacing: 0.14em; }
    .tx-color--sm {
      font-size: 7pt;
      line-height: 1.2;
      letter-spacing: 0.1em;
      white-space: normal;
      overflow-wrap: anywhere;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }

    .tx-code {
      min-width: 0;
      font-weight: 800;
      line-height: 1.15;
      letter-spacing: -0.02em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-transform: uppercase;
    }
    .tx-code--lg { font-size: 17pt; }
    .tx-code--md { font-size: 12.5pt; }
    .tx-code--sm { font-size: 10pt; }
    .tx-code--xs {
      font-size: 8pt;
      line-height: 1.15;
      white-space: normal;
      overflow-wrap: anywhere;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }

    .tx-divider--hidden { visibility: hidden; }

    .tx-main--qr {
      grid-template-columns: minmax(0, 1fr) ${L.ruleMM}mm auto ${L.ruleMM}mm auto ${L.qrBoxMM}mm;
      column-gap: ${L.qrColumnGapMM}mm;
    }
    .tx-qr { width: ${L.qrBoxMM}mm; height: ${L.qrBoxMM}mm; }
    .tx-qr svg { width: 100%; height: 100%; display: block; }

    .tx-divider {
      align-self: center;
      width: ${L.ruleMM}mm;
      height: ${L.dividerHeightMM}mm;
      background: rgba(17, 17, 17, 0.28);
    }

    .tx-size {
      height: ${L.badgeSizeMM}mm;
      min-width: ${L.badgeSizeMM}mm;
      max-width: ${L.badgeMaxWidthMM}mm;
      padding: 0 1mm;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 2.6mm;
      border: 0.24mm solid transparent;
      font-weight: 800;
      line-height: 1.15;
      letter-spacing: -0.03em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-transform: uppercase;
    }
    .tx-size--lg { font-size: 16pt; }
    .tx-size--md { font-size: 13pt; }
    .tx-size--sm { font-size: 10pt; }
    .tx-size--xs { font-size: 7.5pt; }
    .tx-size--ink { background: #111111; color: #ffffff; }
    .tx-size--navy { background: #1c2335; color: #ffffff; }
    .tx-size--rose { background: #f6d9d3; color: #111111; border-color: rgba(17, 17, 17, 0.2); }
    .tx-size--light { background: #ffffff; color: #111111; border-color: #111111; }

    .tx-price-block {
      display: grid;
      gap: 0.6mm;
      justify-items: start;
    }
    .tx-price-block--empty { visibility: hidden; }

    .tx-price-label {
      font-size: 6pt;
      font-weight: 500;
      line-height: 1.3;
      letter-spacing: 0.12em;
      color: rgba(17, 17, 17, 0.72);
    }

    .tx-price {
      font-weight: 800;
      line-height: 1.15;
      letter-spacing: -0.02em;
      white-space: nowrap;
      min-height: 1em;
    }
    .tx-price--lg { font-size: 15pt; }
    .tx-price--sm { font-size: 12pt; }

    .tx-barcode-shell {
      align-self: end;
      display: grid;
      gap: ${L.barcodeSpacingMM}mm;
    }

    .tx-barcode {
      margin-inline: auto;
      width: 100%;
      overflow: hidden;
      padding: 0 ${L.barcodeSidePaddingMM}mm;
    }

    .tx-barcode svg {
      width: 100% !important;
      height: ${L.barcodeHeightMM}mm !important;
      display: block;
    }

    .tx-barcode-text {
      font-size: ${L.barcodeTextPt}pt;
      line-height: 1.15;
      font-weight: 500;
      letter-spacing: 0.1em;
      text-align: center;
      word-break: break-all;
      color: rgba(17, 17, 17, 0.66);
    }
    /* Short labels: same design, tighter rows and smaller type. */
    .label-card--compact { border-radius: 1.4mm; }
    .label-card--compact .label-card-inner {
      padding-top: calc(var(--padding-top) + ${C.topInsetMM}mm);
      grid-template-rows: ${C.mainRowMM}mm 1fr;
      row-gap: ${C.rowGapMM}mm;
    }
    .label-card--compact .tx-main { height: ${C.mainRowMM}mm; column-gap: ${C.columnGapMM}mm; }
    .label-card--compact .tx-main--qr {
      grid-template-columns: minmax(0, 1fr) ${C.ruleMM}mm auto ${C.ruleMM}mm auto ${C.qrBoxMM}mm;
      column-gap: ${C.qrColumnGapMM}mm;
    }
    .label-card--compact .tx-qr { width: ${C.qrBoxMM}mm; height: ${C.qrBoxMM}mm; }
    .label-card--compact .tx-id { gap: 1mm; }
    .label-card--compact .tx-color { padding-bottom: 1mm; }
    .label-card--compact .tx-color--lg { font-size: 9pt; }
    .label-card--compact .tx-color--md { font-size: 7.5pt; }
    .label-card--compact .tx-color--sm { font-size: 6pt; line-height: 1.1; padding-bottom: 0.7mm; }
    .label-card--compact .tx-id:has(.tx-color--sm) { gap: 0.8mm; }
    .label-card--compact .tx-code--lg { font-size: 14pt; }
    .label-card--compact .tx-code--md { font-size: 11pt; }
    .label-card--compact .tx-code--sm { font-size: 9pt; }
    .label-card--compact .tx-code--xs { font-size: 7pt; }
    .label-card--compact .tx-divider { height: ${C.dividerHeightMM}mm; }
    .label-card--compact .tx-size { height: ${C.badgeSizeMM}mm; min-width: ${C.badgeSizeMM}mm; border-radius: 2.2mm; }
    .label-card--compact .tx-size--lg { font-size: 13pt; }
    .label-card--compact .tx-size--md { font-size: 10.5pt; }
    .label-card--compact .tx-size--sm { font-size: 8.5pt; }
    .label-card--compact .tx-size--xs { font-size: 6.5pt; }
    .label-card--compact .tx-price-label { font-size: 5.5pt; }
    .label-card--compact .tx-price--lg { font-size: 13pt; }
    .label-card--compact .tx-price--sm { font-size: 10.5pt; }
    .label-card--compact .tx-barcode-shell { gap: ${C.barcodeSpacingMM}mm; }
    .label-card--compact .tx-barcode svg { height: ${C.barcodeHeightMM}mm !important; }
    .label-card--compact .tx-barcode-text { font-size: ${C.barcodeTextPt}pt; }
`;

export const textileModern = Object.freeze({
  id: "textile-modern",
  renderCard: renderTextileCard,
  cardCss: TEXTILE_CARD_CSS,
  getBarcodeDrawableWidthMM: getTextileBarcodeDrawableWidthMM,
  validateFit: validateTextileFit,
  // Labels of this design may carry a product QR (see getTextileQrPlan).
  usesQr: true,
});
